import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, AlertTriangle, ExternalLink, History, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import axiosInstance from '@/utils/axiosInstance';

const COUNTRY_FLAG = {
  BE: '🇧🇪', NL: '🇳🇱', FR: '🇫🇷', DE: '🇩🇪', UK: '🇬🇧',
};

// Same convention as Products.jsx:
//   evolution = discount_past - discount_today
//   negative → discount got HIGHER today → RED (situation worsened)
//   positive → discount got LOWER today → GREEN (situation improved)
function EvolutionCell({ value }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const n = parseFloat(value);
  if (n === 0) return <span className="text-muted-foreground">0%</span>;
  const isDiscountIncrease = n < 0;
  const displayValue = Math.abs(n).toFixed(2);
  return (
    <span className={`font-medium ${isDiscountIncrease ? 'text-red-600' : 'text-green-600'}`}>
      {isDiscountIncrease ? '+' : '-'}{displayValue}%
    </span>
  );
}

export default function AlertReport() {
  const { id, date } = useParams();
  const { t } = useTranslation('alerts');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [filterReseller, setFilterReseller] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [sortBy, setSortBy] = useState({ field: 'discount', dir: 'desc' });

  const handleSort = (field) => {
    setSortBy(prev => {
      if (prev.field !== field) return { field, dir: 'desc' };
      if (prev.dir === 'desc') return { field, dir: 'asc' };
      return { field: 'discount', dir: 'desc' };
    });
  };

  const SortIcon = ({ field }) => {
    if (sortBy.field !== field) {
      return <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-40" />;
    }
    return sortBy.dir === 'desc'
      ? <ArrowDown className="inline h-3 w-3 ml-1" />
      : <ArrowUp className="inline h-3 w-3 ml-1" />;
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    setFilterReseller('all');
    setFilterCountry('all');
    axiosInstance
      .get(`/api/alerts/${id}/report/`, { params: date ? { date } : {} })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.status === 404 ? 'notFound' : 'fetch'))
      .finally(() => setLoading(false));
  }, [id, date]);

  const formatDate = (iso) => {
    if (!iso) return '';
    if (iso.length === 8) {
      return `${iso.slice(6, 8)}/${iso.slice(4, 6)}/${iso.slice(0, 4)}`;
    }
    return iso;
  };

  // Aggregate stats per reseller (sorted by violation count desc)
  const resellerStats = useMemo(() => {
    if (!data?.violations) return [];
    const stats = new Map();
    for (const v of data.violations) {
      const key = v.reseller_domain || '—';
      if (!stats.has(key)) {
        stats.set(key, { domain: key, count: 0, maxDiscount: 0, countries: new Set() });
      }
      const s = stats.get(key);
      s.count += 1;
      if (v.discount > s.maxDiscount) s.maxDiscount = v.discount;
      if (v.country) s.countries.add(v.country);
    }
    return Array.from(stats.values())
      .map(s => ({ ...s, countries: Array.from(s.countries) }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const availableCountries = useMemo(() => {
    if (!data?.violations) return [];
    return Array.from(new Set(data.violations.map(v => v.country).filter(Boolean)));
  }, [data]);

  const filteredViolations = useMemo(() => {
    if (!data?.violations) return [];
    const filtered = data.violations.filter(v => {
      if (filterReseller !== 'all' && v.reseller_domain !== filterReseller) return false;
      if (filterCountry !== 'all' && v.country !== filterCountry) return false;
      return true;
    });

    const { field, dir } = sortBy;
    const mult = dir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (av < bv) return -1 * mult;
      if (av > bv) return 1 * mult;
      return 0;
    });
  }, [data, filterReseller, filterCountry, sortBy]);

  const hasFilters = filterReseller !== 'all' || filterCountry !== 'all';

  return (
    <Layout>
      <PageHeader title={
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/alerts')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">{t('report.title')}</h1>
        </div>
      } />

      <div className="space-y-6">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center space-y-2">
              <AlertTriangle className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="text-base font-medium">
                {error === 'notFound' ? t('report.notFound') : t('report.error')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary card */}
            <Card className="border-amber-200">
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {t('report.alertLabel')}
                    </p>
                    <h2 className="text-xl font-semibold">{data.alert.name}</h2>
                  </div>
                  <Link to={`/alerts/${id}`}>
                    <Button variant="outline" size="sm" className="gap-2">
                      <History className="h-4 w-4" />
                      {t('report.viewHistory')}
                    </Button>
                  </Link>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('report.dateLabel')}</p>
                    <p className="font-semibold">{formatDate(data.date) || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('report.thresholdLabel')}</p>
                    <p className="font-semibold">{data.threshold_pct}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('report.countriesLabel')}</p>
                    <p className="font-semibold">
                      {data.alert.countries.length === 0
                        ? t('allCountries')
                        : data.alert.countries.map(c => COUNTRY_FLAG[c] || c).join(' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('report.violationsLabel')}</p>
                    <p className="font-bold text-2xl text-red-700">{data.count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {data.count === 0 ? (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="py-10 text-center space-y-2">
                  <div className="text-4xl">✅</div>
                  <p className="text-lg font-semibold text-green-900">
                    {t('report.noViolationsTitle')}
                  </p>
                  <p className="text-sm text-green-800">
                    {t('report.noViolationsDescription')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* At-a-glance recap by reseller */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">{t('report.byResellerTitle')}</h3>
                      <span className="text-xs text-muted-foreground">
                        {t('report.byResellerHint')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {resellerStats.map(s => {
                        const isActive = filterReseller === s.domain;
                        return (
                          <button
                            key={s.domain}
                            type="button"
                            onClick={() =>
                              setFilterReseller(isActive ? 'all' : s.domain)
                            }
                            className={`text-left rounded-lg border p-3 transition-colors ${
                              isActive
                                ? 'border-amber-400 bg-amber-50'
                                : 'border-border hover:border-amber-200 hover:bg-amber-50/50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm truncate">
                                {s.domain}
                              </span>
                              <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border-red-200">
                                {s.count}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>
                                {t('report.worstDiscount')}: <strong className="text-red-700">-{s.maxDiscount}%</strong>
                              </span>
                              <span className="text-base">
                                {s.countries.map(c => COUNTRY_FLAG[c] || c).join(' ')}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Filters + Violations table */}
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                      <div className="flex items-end gap-3 flex-wrap">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">
                            {t('report.filterReseller')}
                          </p>
                          <Select value={filterReseller} onValueChange={setFilterReseller}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{t('report.filterAll')}</SelectItem>
                              {resellerStats.map(s => (
                                <SelectItem key={s.domain} value={s.domain}>
                                  {s.domain} ({s.count})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">
                            {t('report.filterCountry')}
                          </p>
                          <Select value={filterCountry} onValueChange={setFilterCountry}>
                            <SelectTrigger className="w-[160px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{t('report.filterAll')}</SelectItem>
                              {availableCountries.map(c => (
                                <SelectItem key={c} value={c}>
                                  {COUNTRY_FLAG[c] || ''} {c}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {hasFilters && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFilterReseller('all');
                              setFilterCountry('all');
                            }}
                            className="gap-1 text-muted-foreground"
                          >
                            <X className="h-3 w-3" />
                            {t('report.clearFilters')}
                          </Button>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {t('report.showing', {
                          count: filteredViolations.length,
                          total: data.count,
                        })}
                      </span>
                    </div>

                    <div className="border rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('report.col.product')}</TableHead>
                            <TableHead>{t('report.col.reseller')}</TableHead>
                            <TableHead>{t('report.col.country')}</TableHead>
                            <TableHead
                              className="text-right cursor-pointer select-none hover:text-foreground"
                              onClick={() => handleSort('compare_at_price')}
                            >
                              {t('report.col.rrp')}<SortIcon field="compare_at_price" />
                            </TableHead>
                            <TableHead
                              className="text-right cursor-pointer select-none hover:text-foreground"
                              onClick={() => handleSort('price_reseller')}
                            >
                              {t('report.col.price')}<SortIcon field="price_reseller" />
                            </TableHead>
                            <TableHead
                              className="text-right cursor-pointer select-none hover:text-foreground"
                              onClick={() => handleSort('discount')}
                            >
                              {t('report.col.discount')}<SortIcon field="discount" />
                            </TableHead>
                            <TableHead
                              className="text-center cursor-pointer select-none hover:text-foreground"
                              onClick={() => handleSort('evolution_yesterday')}
                            >
                              {t('report.col.vsYesterday')}<SortIcon field="evolution_yesterday" />
                            </TableHead>
                            <TableHead
                              className="text-center cursor-pointer select-none hover:text-foreground"
                              onClick={() => handleSort('evolution_last_week')}
                            >
                              {t('report.col.vsLastWeek')}<SortIcon field="evolution_last_week" />
                            </TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredViolations.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-8">
                                {t('report.noFilterResults')}
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredViolations.map((v, i) => (
                              <TableRow key={`${v.shopify_id}-${v.url}-${i}`}>
                                <TableCell className="max-w-xs">
                                  <div className="flex items-center gap-2">
                                    {v.v_image_link && (
                                      <img
                                        src={v.v_image_link}
                                        alt=""
                                        className="h-8 w-8 rounded object-cover shrink-0"
                                      />
                                    )}
                                    <span className="font-medium truncate">{v.p_title || '—'}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm">{v.reseller_domain}</TableCell>
                                <TableCell>
                                  <span className="text-base">{COUNTRY_FLAG[v.country] || v.country}</span>
                                </TableCell>
                                <TableCell className="text-right text-muted-foreground line-through">
                                  €{v.compare_at_price?.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-semibold text-red-700">
                                  €{v.price_reseller?.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border-red-200 font-bold">
                                    -{v.discount}%
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center text-sm">
                                  <EvolutionCell value={v.evolution_yesterday} />
                                </TableCell>
                                <TableCell className="text-center text-sm">
                                  <EvolutionCell value={v.evolution_last_week} />
                                </TableCell>
                                <TableCell>
                                  <a href={v.url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                  </a>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
